import { BUTTON_SIZE, BUTTON_TYPE, DSButton, DSModal } from '@repo/design-system/index'
import { QIcon_download, QIcon_exclamation } from '@repo/icons/index'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import Parser from 'html-react-parser'
import fetch from 'isomorphic-unfetch'
import { useEffect, useRef, useState } from 'react'
import { FileUploader } from 'react-drag-drop-files'
import 'react-reflex/styles.css'
import { toast } from 'react-toastify'
import apis from '../../../modules/apis'
import { getJwtToken } from '../../../modules/auth'
import { axiosDelete } from '../../../modules/axiosHelper'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import { n_common, n_lesson_prep, n_upcoming_lessons, s_preparation } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { lessonPreparationQueryApiV5 } from '../../../modules/react-query/queryApis'
import { COLORS } from '../../../modules/vars'
import NoSSR from '../../common/NoSSR'

type FileUploadBodyPropsType = {
  materialRef?: any
  lesson?: any
}

export const paneMaxHeight = 'calc(100vh - 120px)'

export const FileUploadBody = ({ materialRef, lesson }: FileUploadBodyPropsType) => {
  const { t, isLocaleKO } = useTranslation()
  const [lessonId, setLessonId] = useState(lesson.id)
  const [file, setFile] = useState()
  const [materialLink, setMaterialLink] = useState(lesson.material_link)
  const [activeKey, setActiveKey] = useState('upload')
  const [showUploadInput, setShowUploadInput] = useState(false)
  const fileRef = useRef(null)
  const queryClient = useQueryClient()
  const isWebview = useIsWebview()

  useEffect(() => {
    if (lesson) {
      if (lesson.material_link) {
        setShowUploadInput(false)
      } else {
        setShowUploadInput(true)
      }
      setMaterialLink(convertUrl(lesson.file_extension, lesson.material_link))
    }
  }, [])

  // 파일 업로드시 자동 제출
  useEffect(() => {
    if (file) {
      handleSubmitFile()
    }
  }, [file])

  const convertUrl = (fileExtension, materialLink) => {
    if (materialLink) {
      if (fileExtension == '.pdf') {
        return `${materialLink}#zoom=50`
      } else {
        const encodedUrl = encodeURIComponent(materialLink)
        return `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`
      }
    } else {
      return null
    }
  }

  const handleFileChange = (e) => {
    console.log(e.target)

    const file = e.target !== undefined ? e.target?.files[0] : e
    if (!file) {
      alert(t(s_preparation.your_upload_has_failed))
      fileRef.current.value = null
      return
    }
    const fileSize = file.size / 1024 / 1024
    if (fileSize > 10) {
      alert(t(s_preparation.the_size_of_file_is_less_than_10mb))
      fileRef.current.value = null
      return
    }

    const _validFileExtensions = ['.pptx', '.pdf', '.ppt', '.doc', '.docx']
    let valid = false
    for (let j = 0; j < _validFileExtensions.length; j++) {
      const sCurExtension = _validFileExtensions[j]
      if (
        file.name.substr(file.name.length - sCurExtension.length, sCurExtension.length).toLowerCase() ==
        sCurExtension.toLowerCase()
      ) {
        valid = true
        break
      }
    }

    if (!valid) {
      alert(t(s_preparation.unsupported_file_format))
      fileRef.current.value = null
      return
    }

    setFile(file)
  }

  const handleSubmitFile = async () => {
    const pathParams = { lessonId }
    if (!file) {
      alert(t(s_preparation.upload_your_file_first))
    }

    const formData = new FormData()
    formData.append('pdf', file)

    await fetch(apis.lessons.upload_file(lessonId), {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + getJwtToken(),
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          toast(data.message)
          console.log(data, 'response')
          setMaterialLink(convertUrl(data.file_extension, data.material_link))
          setShowUploadInput(false)
          setFile(null)
          setActiveKey('material')
          queryClient.invalidateQueries(lessonPreparationQueryApiV5.getQueryKey({ pathParams }))
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const [showPopup, setShowPopup] = useState<boolean>(false)
  const handleDelete = async (e) => {
    console.log('delete file')
    e.preventDefault()
    setShowPopup(false) // 삭제 전 팝업 닫기
    const response = await axiosDelete(apis.lessons.upload_file(lessonId))
    console.log(response, 'response')
    if (response.success) {
      toast(response.message)
      window.location.reload()
      setFile(null)
    }
  }

  const openPopup: React.MouseEventHandler<HTMLDivElement | HTMLButtonElement> = (e) => {
    e.stopPropagation()
    setShowPopup(true)
  }

  // 그냥 팝업 닫기
  const closePopup: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation()
    setShowPopup(false)
  }

  const DeletePopupBody = ({ showPopup, setShowPopup }) => {
    return (
      <DSModal id="TextOnlySMTwoButtonHorizontalPopup" isOpen={showPopup} setIsOpen={setShowPopup} size="lg">
        <DSModal.Title>{t(n_lesson_prep.upload_popup_save_title)}</DSModal.Title>
        <DSModal.Content>{t(n_lesson_prep.upload_popup_save_info)}</DSModal.Content>
        <DSModal.ButtonContainer direction="horizontal">
          <DSButton
            size={BUTTON_SIZE.MD}
            type={BUTTON_TYPE.OUTLINE}
            customBgColor={COLORS.WHITE}
            onClick={(e) => closePopup(e)}
          >
            <DSButton.Label>{t(n_common.btn_cancel)}</DSButton.Label>
          </DSButton>
          <DSButton
            size={BUTTON_SIZE.MD}
            type={BUTTON_TYPE.SOLID}
            className="text-white"
            onClick={(e) => handleDelete(e)}
          >
            <DSButton.Label>삭제</DSButton.Label>
          </DSButton>
        </DSModal.ButtonContainer>
      </DSModal>
    )
  }

  return (
    <div className="no-scrollbar flex h-full flex-col" style={!isWebview && { height: 'calc(100vh - 120px)' }}>
      <div className="h-full">
        {materialLink ? (
          <div className="h-full w-full items-center justify-center">
            {isWebview ? (
              <div className="mb-[16px] flex flex-col p-16">
                <span className="text-[20px] font-bold">{t(n_upcoming_lessons.menu_option1)}</span>
                <span className="text-[14px] font-medium">{t(n_lesson_prep.upload_desc3)}</span>
              </div>
            ) : (
              <div className="mb-[16px] flex flex-row space-x-4 rounded-[4px] bg-red-50 p-12">
                <QIcon_exclamation className="h-[20px] w-[20px]" />
                <div className="flex flex-col space-y-[4px]">
                  <span className="text-14 font-bold text-black">{t(n_lesson_prep.upload_desc1)}</span>
                  <span className="text-14 font-medium text-black">{t(s_preparation.upload_material_notice)}</span>
                </div>
              </div>
            )}
            <div className="mt-[20px] h-[8px] w-full bg-gray-50"></div>
            <iframe key={materialLink} ref={materialRef} src={materialLink} className="h-full w-full" />
          </div>
        ) : (
          <div className="flex w-full flex-col space-y-12">
            {isWebview ? (
              <div className="flex flex-col p-16">
                <span className="text-[20px] font-bold">{t(n_lesson_prep.tab_upload)}</span>
                <span className="text-[14px] font-medium">{t(n_lesson_prep.upload_desc3)}</span>
              </div>
            ) : (
              <>
                <div className="flex flex-col rounded-[4px] bg-red-50 p-12">
                  <div className="flex flex-row space-x-[4px]">
                    <QIcon_exclamation className="h-20 w-20" />
                    <span className="text-14 font-bold text-black">{t(n_lesson_prep.upload_desc1)}</span>
                  </div>
                  <div className="pl-24">
                    <span className="text-14 font-medium text-black">{t(s_preparation.upload_material_notice)}</span>
                  </div>
                </div>
                <div className="flex w-full flex-col items-center justify-center rounded-[8px] bg-gray-50 p-16 text-center text-gray-700">
                  <FileUploader style={{ width: '100%' }} ref={fileRef} handleChange={(e) => handleFileChange(e)}>
                    <div className="mt-4 flex w-full flex-col items-center justify-center">
                      <QIcon_download className="h-24 w-24 text-gray-400" />
                      <span className="text-14 font-medium text-gray-400">{t(n_lesson_prep.upload_guide)}</span>
                      <div className="mt-12 flex items-center justify-center">
                        {/* <div className="min-w-4 flex h-4 items-center"></div> */}
                        <div className="mx-auto flex">
                          <button
                            id="id_file_upload"
                            className="flex rounded-[7px] border-[1.5px] border-gray-300 px-28 py-12 text-black"
                          >
                            {t(n_lesson_prep.upload_select)}
                          </button>
                        </div>
                        {/* <div className="min-w-4 flex h-4 items-center"></div> */}
                      </div>
                    </div>
                  </FileUploader>
                </div>
              </>
            )}
            <div
              className={classNames(
                [isWebview ? '' : 'bg-gray-50'],
                'flex flex-col items-start justify-start space-y-8 rounded-[8px] p-16 text-gray-700'
              )}
            >
              <div>
                <span className="text-16 font-bold text-black">{t(n_lesson_prep.upload_info_title)}</span>
              </div>
              <ul className="pl-16">
                <li className="text-14 list-disc font-medium text-black">{t(n_lesson_prep.upload_info1)}</li>
                <li className="text-14 list-disc font-medium text-black">{t(n_lesson_prep.upload_info2)}</li>
                <li className="text-14 list-disc font-medium text-black">{t(n_lesson_prep.upload_info3)}</li>
                <li className="text-14 list-disc font-medium text-black">{t(n_lesson_prep.upload_info4)}</li>
                <li className="text-14 list-disc font-medium text-black">{t(n_lesson_prep.upload_info5)}</li>
              </ul>
            </div>
          </div>
        )}
        {lesson.course.event_course && (
          <a
            rel="noreferrer"
            className="btn btn-lg mt-32 mr-3 bg-purple-500 text-white"
            href="https://ringlecontents.s3.ap-northeast-2.amazonaws.com/challenge-template.zip"
            target="_blank"
          >
            {t(s_preparation.download_template)}
          </a>
        )}
        {lesson.course.event_course && (
          <div style={{ maxHeight: 'calc(100vh - 360px)', height: '100vh' }} className="mt-5 overflow-y-auto p-5">
            <NoSSR>
              <div>
                {isLocaleKO
                  ? lesson.course.summary_kor && Parser(lesson.course.summary_kor)
                  : lesson.course.summary_eng && Parser(lesson.course.summary_eng)}
              </div>
            </NoSSR>
          </div>
        )}
        {/* 업로드 후 파일 보이지 않을 시 - 새로고침해주세요 */}
      </div>
      {materialLink && !isWebview && (
        <div className="sticky bottom-0 flex w-full bg-white p-[32px]">
          <div className="text-14 mr-[8px] flex items-center text-gray-700">{t(s_preparation.if_no_screen)}</div>
          <div className="ml-auto flex space-x-8">
            <div>
              <DSButton
                className="text-14 text-purple-600"
                customBorderColor={COLORS.PURPLE600}
                size={BUTTON_SIZE.MD}
                type={BUTTON_TYPE.OUTLINE}
                onClick={(e) => openPopup(e)}
              >
                <DSButton.Label className="whitespace-nowrap" customTxtColor={COLORS.PURPLE600}>
                  {t(n_lesson_prep.upload_btn_delete)}
                </DSButton.Label>
              </DSButton>
            </div>
            <div>
              <DSButton
                className="text-14 text-purple-600"
                customBorderColor={COLORS.PURPLE600}
                size={BUTTON_SIZE.MD}
                type={BUTTON_TYPE.OUTLINE}
                onClick={() => {
                  console.log('자료 업로드')
                }}
              >
                <label className="Body2-14Bd cursor-pointer whitespace-nowrap text-purple-600" htmlFor="id_file_upload">
                  {t(n_lesson_prep.upload_btn_other)}
                </label>
              </DSButton>
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            id="id_file_upload"
            name="pdf"
            onChange={(e) => handleFileChange(e)}
          />
        </div>
      )}
      <DeletePopupBody showPopup={showPopup} setShowPopup={setShowPopup} />
    </div>
  )
}
